import { DataTable, type Column, Pill, ProgressBar } from "@/design/components";
import { cn } from "@/design/cn";
import { useProjects, useOrgIndex, useMoney } from "@/domain/hooks";
import type { Project } from "@/domain/types";

export type Rag = "green" | "amber" | "red" | "neutral";

export type ProjectHealth = {
  project: Project;
  contractor: string;
  onTime: { rag: Rag; label: string };
  onBudget: { rag: Rag; label: string };
  evidence: { rag: Rag; label: string };
  overall: Rag;
};

/** Derive per-project health from the seed (schedule drift, spend pace, evidence coverage). */
export function computeHealth(project: Project, contractor: string): ProjectHealth {
  if (project.status === "planned" || project.status === "draft") {
    const neutral = { rag: "neutral" as Rag, label: "Not started" };
    return { project, contractor, onTime: neutral, onBudget: neutral, evidence: neutral, overall: "neutral" };
  }
  const drift = project.scheduleDriftDays ?? 0;
  const onTime: { rag: Rag; label: string } =
    drift <= 0 ? { rag: "green", label: `${Math.abs(drift)}d ahead` } :
    drift <= 7 ? { rag: "amber", label: `+${drift}d` } :
    { rag: "red", label: `+${drift}d` };

  const consumed = project.budgeted ? project.spent / project.budgeted : 0;
  const planPct = 0.42;
  const delta = consumed - planPct;
  const onBudget: { rag: Rag; label: string } =
    delta > 0.08 ? { rag: "red", label: `${Math.round(delta * 100)}% over pace` } :
    delta > 0.03 ? { rag: "amber", label: `${Math.round(delta * 100)}% over pace` } :
    { rag: "green", label: delta < 0 ? `${Math.abs(Math.round(delta * 100))}% under` : "On pace" };

  const evPct = project.spent ? project.evidenced / project.spent : 0;
  const evidence: { rag: Rag; label: string } =
    evPct >= 0.8 ? { rag: "green", label: `${Math.round(evPct * 100)}% covered` } :
    evPct >= 0.6 ? { rag: "amber", label: `${Math.round(evPct * 100)}% covered` } :
    { rag: "red", label: `${Math.round(evPct * 100)}% covered` };

  const score = (r: Rag) => (r === "red" ? 2 : r === "amber" ? 1 : 0);
  const worst = Math.max(score(onTime.rag), score(onBudget.rag), score(evidence.rag));
  const overall: Rag = worst === 2 ? "red" : worst === 1 ? "amber" : "green";
  return { project, contractor, onTime, onBudget, evidence, overall };
}

export function useProjectHealth(projects?: Project[]): ProjectHealth[] {
  const all = useProjects();
  const orgs = useOrgIndex();
  return (projects ?? all).map((p) => computeHealth(p, orgs[p.leadContractorId]?.name ?? "—"));
}

const RAG_COLOR: Record<Rag, string> = {
  green: "bg-ok",
  amber: "bg-warn",
  red: "bg-bad",
  neutral: "bg-faint",
};

export function HealthCell({ rag, label }: { rag: Rag; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", RAG_COLOR[rag])} aria-hidden />
      <span className={cn("text-sm tabular-nums", rag === "red" ? "text-bad" : rag === "amber" ? "text-warn" : "text-mute")}>{label}</span>
    </span>
  );
}

export function ProjectHealthTable({ onOpen, projects }: { onOpen?: (p: Project) => void; projects?: Project[] }) {
  const rows = useProjectHealth(projects);
  const money = useMoney();

  const columns: Column<ProjectHealth>[] = [
    {
      key: "name",
      header: "Fire station",
      cell: (h) => (
        <div className="flex items-center gap-2.5">
          <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", RAG_COLOR[h.overall])} aria-hidden />
          <div>
            <p className="font-medium text-ink">{h.project.name}</p>
            <p className="text-xs text-faint">{h.project.district} · {h.project.currentPhase}</p>
          </div>
        </div>
      ),
    },
    { key: "contractor", header: "Contractor", cell: (h) => <span className="text-mute">{h.contractor}</span> },
    { key: "onTime", header: "On time", cell: (h) => <HealthCell {...h.onTime} /> },
    { key: "onBudget", header: "On budget", cell: (h) => <HealthCell {...h.onBudget} /> },
    { key: "evidence", header: "Evidence", cell: (h) => <HealthCell {...h.evidence} /> },
    {
      key: "spent",
      header: "Spent of budget",
      align: "num",
      cell: (h) => {
        const ratio = h.project.budgeted ? h.project.spent / h.project.budgeted : 0;
        const pct = Math.round(ratio * 100);
        const tone = h.onBudget.rag === "red" ? "bad" : h.onBudget.rag === "amber" ? "warn" : "brand";
        return (
          <div className="ml-auto w-28">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-sm font-medium text-ink">{pct}%</span>
              <span className="text-[11px] text-faint">{money(h.project.spent)}</span>
            </div>
            <ProgressBar value={ratio} tone={tone} className="mt-1" />
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (h) => (
        <Pill variant={h.overall === "green" ? "success" : h.overall === "amber" ? "warn" : h.overall === "red" ? "danger" : "neutral"} dot>
          {h.overall === "green" ? "On track" : h.overall === "amber" ? "Watch" : h.overall === "red" ? "At risk" : "Planned"}
        </Pill>
      ),
    },
  ];

  return <DataTable columns={columns} rows={rows} rowKey={(h) => h.project.id} onRowClick={onOpen ? (h) => onOpen(h.project) : undefined} />;
}
